<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { getAutomationSettings, getMaintenanceOverview, saveAutomationSettings } from '../api'
import AutomationMaintenanceWizard from '../components/automation/AutomationMaintenanceWizard.vue'
import AutomationStorageSummary from '../components/automation/AutomationStorageSummary.vue'
import { useI18n } from '../i18n'
import { chooseNaftaPosSource, hasPermission, session } from '../session'
import NaftaPosInstanceSelector from '../components/NaftaPosInstanceSelector.vue'
import { activeNaftaPosInstance } from '../platform/context'

const { locale, t } = useI18n()
const stations = ref([])
const activeStation = computed(() => activeNaftaPosInstance(session.platformContext))
const stationId = computed(() => activeStation.value?.enabled ? String(activeStation.value.id) : '')
const switching = ref(false)
const warehouse = ref({ available: true })
const overview = ref(null)
const loading = ref(true)
const overviewLoading = ref(false)
const saving = ref(false)
const error = ref('')
const notice = ref('')
const activeTab = ref('maintenance')
const form = ref({ enabled: false, intervalMinutes: 15, version: 0 })
const canConfigure = computed(() => hasPermission('automation.state.configure'))
const canPreview = computed(() => hasPermission('maintenance.preview'))
const canExecute = computed(() => hasPermission('maintenance.execute'))
const canResume = computed(() => hasPermission('maintenance.resume'))

const selected = computed(() => stations.value.find((item) => String(item.stationId) === stationId.value) ?? null)
let loadRequest = 0
let overviewRequest = 0
let disposed = false
const sourceScope = () => JSON.stringify([session.user?.id ?? session.user?.username,
  session.user?.accessVersion, session.platformContext?.activeStationId,
  activeStation.value?.id, activeStation.value?.enabled])
const currentScope = (scope) => !disposed && scope === sourceScope()

function copySelected() {
  if (!selected.value) return
  form.value = {
    enabled: selected.value.enabled,
    intervalMinutes: selected.value.intervalMinutes,
    version: selected.value.version,
  }
  error.value = ''
  notice.value = ''
}

async function loadOverview() {
  const request = ++overviewRequest, scope = sourceScope(), sourceId = stationId.value
  const isCurrent = () => request === overviewRequest && currentScope(scope)
  overview.value = null
  if (!stationId.value || !warehouse.value.available) {
    overviewLoading.value = false
    return
  }
  overviewLoading.value = true
  try {
    const result = await getMaintenanceOverview(sourceId)
    if (isCurrent()) overview.value = result
  } catch (cause) {
    if (!isCurrent()) return
    overview.value = null
    error.value = cause.message
  } finally {
    if (isCurrent()) overviewLoading.value = false
  }
}

async function load() {
  const request = ++loadRequest, scope = sourceScope()
  const isCurrent = () => request === loadRequest && currentScope(scope)
  overviewRequest++
  stations.value = []
  overview.value = null
  overviewLoading.value = false
  form.value = { enabled: false, intervalMinutes: 15, version: 0 }
  notice.value = ''
  loading.value = true
  error.value = ''
  if (!session.user) { loading.value = false; return }
  try {
    const response = await getAutomationSettings()
    if (!isCurrent()) return
    stations.value = response.stations
    warehouse.value = response.warehouse
    copySelected()
    if (selected.value) await loadOverview()
  } catch (cause) {
    if (isCurrent()) error.value = cause.message
  } finally {
    if (isCurrent()) loading.value = false
  }
}

async function changeStation(sourceId) {
  if (!sourceId || String(sourceId) === stationId.value || switching.value || saving.value) return
  const scope = sourceScope()
  switching.value = true
  overviewRequest++
  overview.value = null
  error.value = ''; notice.value = ''
  try { await chooseNaftaPosSource(sourceId) }
  catch (cause) { if (currentScope(scope)) error.value = cause.message }
  finally { switching.value = false }
}

async function save() {
  if (!stationId.value || !selected.value || saving.value || loading.value || switching.value || !canConfigure.value) return
  const scope = sourceScope(), sourceId = stationId.value
  const interval = Number(form.value.intervalMinutes)
  if (!Number.isInteger(interval) || interval < 1 || interval > 60) {
    error.value = t('automationIntervalInvalid')
    return
  }
  saving.value = true
  error.value = ''
  notice.value = ''
  try {
    const response = await saveAutomationSettings(sourceId, form.value.enabled, interval, form.value.version)
    if (!currentScope(scope)) return
    const index = stations.value.findIndex((item) => item.stationId === response.station.stationId)
    if (index >= 0) stations.value[index] = response.station
    copySelected()
    notice.value = t('automationSaved')
  } catch (cause) {
    if (!currentScope(scope)) return
    error.value = cause.message
    if (cause.status === 409) await load()
  } finally {
    saving.value = false
  }
}

function statusLabel(status) {
  return ({
    DISABLED: t('automationStatusDisabled'), IDLE: t('automationStatusIdle'), QUEUED: t('automationStatusQueued'),
    RUNNING: t('automationStatusRunning'), SUCCEEDED: t('automationStatusSucceeded'), PARTIAL: t('automationStatusPartial'),
    FAILED: t('automationStatusFailed'), RETIRED: t('automationStatusRetired'), UNAVAILABLE: t('automationStatusUnavailable'),
  })[status] ?? status
}

function dateTime(value) {
  return value ? new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—'
}

onMounted(load)
watch(sourceScope, load, { flush: 'sync' })
onBeforeUnmount(() => { disposed = true; loadRequest++; overviewRequest++ })
</script>

<template>
  <section class="automation-page">
    <header class="automation-page-header">
      <div>
        <p class="eyebrow">{{ t('administration') }}</p>
        <h1>{{ t('automationTitle') }}</h1>
        <p class="muted">{{ t('automationHint') }}</p>
      </div>
      <div v-if="selected" class="automation-header-status">
        <span>{{ t('automationCurrentStatus') }}</span>
        <strong><i :class="`status-dot sync-${selected.status.toLowerCase()}`"></i>{{ statusLabel(selected.status) }}</strong>
        <small>{{ t('lastSuccess') }}: {{ dateTime(selected.lastSuccessAt) }}</small>
      </div>
    </header>

    <p v-if="!warehouse.available" class="notice error" role="alert">{{ t('warehouseUnavailable') }}</p>
    <p v-if="!canConfigure && !canPreview" class="notice info" role="status">{{ t('viewOnlyHint') }}</p>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-if="notice" class="notice success" role="status">{{ notice }}</p>
    <p v-if="loading" class="card">{{ t('loading') }}</p>

    <template v-else>
      <section class="card automation-station-bar">
        <NaftaPosInstanceSelector :context="session.platformContext" :disabled="switching || saving" @select="changeStation" />
        <div v-if="overview" class="automation-station-facts">
          <span>{{ t('maintenanceClosedShifts') }} <strong>{{ overview.eligibleShiftCount }}</strong></span>
          <span>{{ t('maintenanceDataVersion') }} <strong>{{ overview.dataVersion }}</strong></span>
        </div>
      </section>

      <nav class="automation-tabs" :aria-label="t('automationSections')">
        <button type="button" :class="{ active: activeTab === 'sync' }" :aria-current="activeTab === 'sync' ? 'page' : undefined" @click="activeTab = 'sync'">{{ t('automationSyncTab') }}</button>
        <button type="button" :class="{ active: activeTab === 'maintenance' }" :aria-current="activeTab === 'maintenance' ? 'page' : undefined" @click="activeTab = 'maintenance'">{{ t('automationMaintenanceTab') }}</button>
      </nav>

      <div v-if="selected && activeTab === 'sync'" class="automation-grid">
        <section class="card automation-form-card">
          <p class="eyebrow">{{ t('automationSettingsEyebrow') }}</p>
          <h2>{{ t('automationSettingsTitle') }}</h2>
          <form class="form-stack" @submit.prevent="save">
            <label class="checkbox automation-switch">
              <input v-model="form.enabled" type="checkbox" :disabled="!selected.profileEnabled || !canConfigure">
              <span>{{ t('automationEnabled') }}</span>
            </label>
            <label>{{ t('syncInterval') }}
              <input v-model.number="form.intervalMinutes" type="number" min="1" max="60" step="1" required :disabled="!canConfigure">
            </label>
            <p v-if="!selected.profileEnabled" class="muted">{{ t('automationProfileDisabled') }}</p>
            <button v-if="canConfigure" class="button" type="submit" :disabled="saving || !selected.profileEnabled">
              {{ saving ? t('saving') : t('saveAutomation') }}
            </button>
          </form>
        </section>
        <section class="card automation-status-card">
          <div class="section-heading"><div><p class="eyebrow">{{ t('automationStatusEyebrow') }}</p><h2>{{ t('syncStatus') }}</h2></div><span class="sync-badge" :class="`sync-${selected.status.toLowerCase()}`">{{ statusLabel(selected.status) }}</span></div>
          <dl class="automation-status-list">
            <div><dt>{{ t('lastAttempt') }}</dt><dd>{{ dateTime(selected.lastAttemptAt) }}</dd></div>
            <div><dt>{{ t('lastSuccess') }}</dt><dd>{{ dateTime(selected.lastSuccessAt) }}</dd></div>
            <div><dt>{{ t('nextRun') }}</dt><dd>{{ dateTime(selected.nextRunAt) }}</dd></div>
          </dl>
          <p v-if="selected.lastError" class="notice error">{{ selected.lastError.message }}</p>
        </section>
      </div>

      <div v-else-if="selected" class="maintenance-layout">
        <AutomationStorageSummary :overview="overview" :loading="overviewLoading" />
        <AutomationMaintenanceWizard v-if="overview && canPreview" :key="sourceScope()" :station-id="stationId" :overview="overview"
          :allow-execute="canExecute" :allow-resume="canResume" @changed="loadOverview" />
        <p v-else-if="overview && !canPreview" class="card muted">{{ t('viewOnlyHint') }}</p>
        <p v-else-if="overviewLoading" class="card">{{ t('loading') }}</p>
      </div>
    </template>
    <p v-if="!loading && !selected" class="card muted">{{ t('chooseNaftaPosWorkplace') }}</p>
  </section>
</template>
